// src/components/landing/CreateSalonModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "../../utils/axiosConfig";
import { Ticket, Sparkles, X, ArrowRight, Trophy, Building2, Megaphone, UserRound } from "lucide-react";
import { CLIENT_TYPE_OPTIONS, getClientTypeMeta } from "../../utils/clientTypes";

/* helpers */
const normalizeSlug = (value) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

/** Pasa "mi-salon-lindo" -> "Mi Salon Lindo" */
const titleizeFromSlug = (slug) =>
  (slug || "")
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
    .trim();

const TYPE_ICONS = {
  venue: Building2,
  producer: Megaphone,
  creator: UserRound,
  sports: Trophy,
  events: Megaphone,
};

const getBillingModelForType = (organizationType) =>
  organizationType === 'venue' ? 'subscription' : 'commission';

const getModelSummary = (organizationType) => {
  if (organizationType === 'venue') {
    return {
      title: 'Salon / Espacio Fisico',
      description: 'Usa una suscripcion mensual para gestionar agenda, reservas, invitados, cobros y herramientas internas.',
    };
  }

  if (organizationType === 'sports') {
    return {
      title: 'Club / Predio Deportivo',
      description: 'Opera bajo comision por inscripciones y actividad deportiva, sin suscripcion mensual.',
    };
  }

  if (organizationType === 'creator') {
    return {
      title: 'Organizador Eventual',
      description: 'Crea eventos puntuales sin suscripcion mensual. El cargo de servicio se aplica sobre las entradas vendidas.',
    };
  }

  return {
    title: 'Productora de Eventos',
    description: 'Opera bajo comision por tickets vendidos, sin suscripcion mensual fija.',
  };
};

const CreateSalonModal = ({ open, onClose, onSuccess, defaultOrganizationType = "venue", hideSelectors = false }) => {
  const [form, setForm] = useState({
    slug: "",
    organizationType: defaultOrganizationType,
    billingModel: getBillingModelForType(defaultOrganizationType),
  });

  const isSports = form.organizationType === 'sports';
  const isVenue = form.organizationType === 'venue';
  const typeMeta = getClientTypeMeta(form.organizationType);
  const modelSummary = getModelSummary(form.organizationType);
  const HeaderIcon = TYPE_ICONS[form.organizationType] || Sparkles;
  const bgGradient = isSports ? "bg-gradient-to-r from-green-500 to-emerald-600" : isVenue ? "bg-brand-gradient" : "bg-gradient-to-r from-cyan-500 to-blue-600";
  const shadowColor = isSports ? "shadow-green-500/20" : isVenue ? "shadow-orange-500/20" : "shadow-cyan-500/20";

  useEffect(() => {
    if (open) {
      setForm(f => ({
        ...f,
        organizationType: defaultOrganizationType,
        billingModel: getBillingModelForType(defaultOrganizationType),
      }));
    }
  }, [open, defaultOrganizationType]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const firstInputRef = useRef(null);

  const slugPreview = useMemo(() => normalizeSlug(form.slug), [form.slug]);
  const salonUrl = `${window.location.origin}/${slugPreview || "<slug>"}`;

  useEffect(() => {
    const expectedBillingModel = getBillingModelForType(form.organizationType);
    if (form.billingModel !== expectedBillingModel) {
      setForm(prev => ({ ...prev, billingModel: expectedBillingModel }));
    }
  }, [form.organizationType, form.billingModel]);

  /* abrir/cerrar modal: bloqueo scroll fondo + ESC + focus */
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => firstInputRef.current?.focus(), 10);
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [open, onClose]);

  if (!open) return null;

  /* handlers */
  const onChange = (e) => {
    const { name, value } = e.target;
    setError("");
    setForm((f) => ({
      ...f,
      [name]: value,
      ...(name === 'organizationType' ? { billingModel: getBillingModelForType(value) } : {}),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    const slug = normalizeSlug(form.slug);
    if (!slug) return setError("Ingresá un slug válido (minúsculas, números y guiones).");

    const nombreAuto = titleizeFromSlug(slug) || "Nuevo Espacio";

    setLoading(true);
    try {
      const { data } = await axios.post("/api/clients/public-create", {
        nombre: nombreAuto,
        slug,
        organizationType: form.organizationType,
        billingModel: form.billingModel,
        phone: "",
        taxId: "00000000000",
        colores: { primario: "#0ea5e9", secundario: "#111827" },
        showServices: form.organizationType !== 'creator',
      });

      if (data?.claimUrl) {
        window.location.href = data.claimUrl;
      } else {
        onSuccess?.({ slug, claimUrl: null });
        onClose?.();
      }
    } catch (err) {
      if (err?.response?.status === 409) setError("Ese slug ya está en uso. Probá con otro.");
      else if (err?.response?.data?.message) setError(err.response.data.message);
      else setError("Error al crear el salón. Intentá nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="
          w-full max-w-md max-h-[95vh] flex flex-col rounded-[40px] bg-[#0A0A0A] border border-white/10 shadow-2xl 
          overflow-hidden animate-fade-in-up relative
        "
      >
        {/* DECORATION */}
        <div className={`absolute -top-24 -right-24 w-48 h-48 rounded-full blur-[80px] pointer-events-none transition-colors duration-700 ${isSports ? 'bg-green-500/20' : 'bg-primary-orange/20'}`} />
        <div className={`absolute -bottom-24 -left-24 w-48 h-48 rounded-full blur-[80px] pointer-events-none transition-colors duration-700 ${isSports ? 'bg-blue-500/10' : 'bg-primary-red/10'}`} />

        <div className="overflow-y-auto w-full flex-1 custom-scrollbar">

        {/* Header */}
        <div className="px-8 pt-8 pb-6 flex items-center justify-between relative z-10">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg transition-all duration-700 ${bgGradient} ${shadowColor}`}>
              <HeaderIcon className="text-white" size={24} />
            </div>
            <div>
              <h2 className="text-2xl font-black tracking-tight text-white leading-none mb-1 transition-all">
                {isSports ? "Crear tu complejo" : isVenue ? "Crear tu salon" : "Crear tu espacio"}
              </h2>
              <p className="text-xs font-bold text-white/30 uppercase tracking-widest transition-all">
                {typeMeta.label}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-all"
          >
            <X size={20} />
          </button>
        </div>

        {/* Contenido */}
        <form className="px-8 pb-8 space-y-8 relative z-10" onSubmit={submit}>
          {/* TIPO DE ORGANIZACION */}
          <div className="space-y-4">
            <label className="block text-[10px] font-black text-white/40 uppercase tracking-[3px] ml-1">
              Tipo de espacio
            </label>
            <div className={`grid ${hideSelectors ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'} gap-4`}>
              {CLIENT_TYPE_OPTIONS
                .filter(option => !hideSelectors || option.value === form.organizationType)
                .map((option) => {
                  const Icon = TYPE_ICONS[option.value] || Sparkles;
                  const selected = form.organizationType === option.value;
                  const accentClass = option.value === 'sports'
                    ? 'border-green-500 bg-green-500/10 shadow-[0_0_20px_rgba(34,197,94,0.2)]'
                    : option.value === 'venue'
                      ? 'border-primary-orange bg-primary-orange/10 shadow-[0_0_20px_rgba(255,100,0,0.2)]'
                      : 'border-cyan-500 bg-cyan-500/10 shadow-[0_0_20px_rgba(6,182,212,0.18)]';
                  const iconClass = option.value === 'sports' ? 'text-green-500' : option.value === 'venue' ? 'text-primary-orange' : 'text-cyan-400';

                  return (
                    <label key={option.value} className={`cursor-pointer border-2 rounded-2xl p-4 flex flex-col gap-2 transition-all ${selected ? accentClass : 'bg-white/5 border-white/10 hover:border-white/20 hover:bg-white/10'} ${hideSelectors ? 'pointer-events-none py-5' : ''}`}>
                      <input type="radio" name="organizationType" value={option.value} checked={selected} onChange={onChange} className="hidden" />
                      <div className="flex items-center gap-3">
                        <Icon size={22} className={selected ? iconClass : 'text-white/40'} />
                        <span className={`font-black uppercase tracking-wider text-sm ${selected ? 'text-white' : 'text-white/40'}`}>{option.label}</span>
                      </div>
                      {!hideSelectors && <span className="text-[11px] text-white/45 font-medium leading-snug">{option.description}</span>}
                    </label>
                  );
                })}
            </div>
          </div>
          {/* MODELO DE FACTURACIÓN */}
          <div className="space-y-4">
            <label className="block text-[10px] font-black text-white/40 uppercase tracking-[3px] ml-1">
              Modelo de Negocio
            </label>
            <div className={`rounded-2xl border-2 p-4 flex flex-col gap-1 ${isSports ? 'bg-green-500/10 border-green-500/40' : isVenue ? 'bg-primary-orange/10 border-primary-orange/40' : 'bg-cyan-500/10 border-cyan-500/40'}`}>
              <span className="font-black uppercase tracking-wider text-sm text-white">
                {modelSummary.title}
              </span>
              <span className="text-[11px] text-white/50 font-medium">
                {modelSummary.description}
              </span>
            </div>
          </div>

          <div className="space-y-4">
            <label className="block text-[10px] font-black text-white/40 uppercase tracking-[3px] ml-1">
              Nombre de tu organización
            </label>

            <div className="relative group">
              <div className={`absolute inset-y-0 left-5 flex items-center pointer-events-none transition-colors ${isSports ? 'group-focus-within:text-green-500' : 'group-focus-within:text-primary-orange'}`}>
                {isSports ? <Trophy size={20} className="text-white/20 transition-colors" /> : <Ticket size={20} className="text-white/20 transition-colors" />}
              </div>
              <input
                ref={firstInputRef}
                name="slug"
                value={form.slug}
                onChange={onChange}
                className={`
                  w-full bg-white/5 border border-white/10 rounded-3xl px-14 py-5 
                  text-white font-bold placeholder:text-white/10 outline-none
                  focus:ring-4 transition-all text-lg
                  ${isSports ? 'focus:border-green-500/50 focus:ring-green-500/10' : 'focus:border-primary-orange/50 focus:ring-primary-orange/10'}
                `}
                required
                placeholder={typeMeta.label}
              />
            </div>

            <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-4 space-y-2">
              <p className="text-[10px] font-bold text-white/20 uppercase tracking-widest leading-none">Tu URL personalizada</p>
              <code className={`block text-sm font-bold break-all transition-colors duration-700 ${isSports ? 'text-green-400' : 'text-primary-orange/80'}`}>
                {salonUrl}
              </code>
              <p className="text-[10px] text-white/30 font-medium italic">
                * Solo minúsculas, números y guiones.
              </p>
            </div>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4 text-red-400 text-sm font-bold flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              {error}
            </div>
          )}

          <div className="space-y-4">
            <button
              disabled={loading}
              className={`
                w-full ${bgGradient} py-6 rounded-[28px] font-black text-white 
                shadow-xl ${shadowColor} hover:scale-[1.02] active:scale-[0.98] 
                transition-all flex items-center justify-center gap-3 group shine-effect
                disabled:opacity-50 disabled:hover:scale-100
              `}
            >
              {loading ? (
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  <span>Redirigiendo...</span>
                </div>
              ) : (
                <>
                  <span>CREAR Y VINCULAR</span>
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>

            <p className="text-[10px] text-center text-white/20 font-bold uppercase tracking-widest leading-relaxed px-4">
              Serás redirigido a Google para vincular tu cuenta como administrador oficial.
            </p>
          </div>
        </form>

        {/* Footer */}
        <div className="px-8 py-6 bg-white/[0.02] border-t border-white/5 flex justify-center mt-2">
          <button
            onClick={onClose}
            className="w-full bg-white/5 hover:bg-white/10 border border-white/10 py-4 rounded-[20px] font-black text-xs text-white uppercase tracking-[2px] transition-all"
          >
            Cancelar
          </button>
        </div>
        </div>
      </div>
    </div>
  );
};

export default CreateSalonModal;

